import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Apple,
  Check,
  ChevronDown,
  GitBranch,
  Loader2,
  MoreVertical,
  Pencil,
  Plus,
  RotateCcw,
  Save,
  Trash2,
  Upload,
} from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  RecipeFoodPickerDialog,
  type PickedFoodPayload,
} from "@/components/settings/RecipeFoodPickerDialog";
import { InfoPopover } from "@/components/settings/InfoPopover";
import {
  RecipeServingsTable,
  type RecipeServing,
  type ServingMode,
} from "@/components/settings/RecipeServingsTable";
import {
  DraftInProgressPill,
  FoodCodeBadge,
} from "@/components/settings/nutrition-shared";
import { TagPickerRow } from "@/components/settings/TagPickerRow";
import { RecipeBrandSelect } from "@/components/settings/RecipeBrandSelect";
import { EditableField } from "@/components/settings/EditableField";
import { SELECT_CONTENT_MAX_H } from "@/components/settings/recipe-select-constants";
import {
  DerivedDietFlagsRow,
  DIET_FLAG_ORDER,
  DIET_FLAG_RULED_OUT_LABELS,
  DietFlagChip,
  type DietFlagKey,
  type DietFlagState,
  type DietFlagStatus,
} from "@/components/settings/DerivedDietFlagsRow";
import { useToast } from "@/components/ui/use-toast";
import { ToastAction } from "@/components/ui/toast";
import {
  NutritionLabelFSSAI,
  type FSSAILabelRow,
} from "@/components/settings/NutritionLabelFSSAI";
import { type EditableNutrientField } from "@/components/settings/EditableNutrientTable";
import { useNutrientCatalog } from "@/hooks/useNutrientCatalog";
import { useFoodServings } from "@/hooks/useNutritionEvaluation";
import { useTagMetadata } from "@/hooks/useNutritionDropdowns";
import { quantityPatternFor } from "@/components/consultation/tabs/nutrition/components/meal-constants";
import {
  useCreateRecipe,
  useDeleteRecipe,
  useDiscardRecipe,
  useRecipe,
  useSubmitRecipe,
  useUpdateRecipe,
} from "@/hooks/useRecipes";
import { queryKeys } from "@/lib/query-keys";
import { useAuthStore } from "@/stores/authStore";
import {
  isRecipeBlockerError,
  isRecipeConflictError,
  type RecipeBlocker,
  type RecipeDetail,
  type RecipeDietFlag,
  type RecipeIngredient,
  type RecipeNutrientProfile,
  type RecipeNutrientProfileRow,
  type RecipeResolvedTag,
  type RecipeWarning,
  type RecipeWritePayload,
} from "@/lib/recipe-api";
import { cn } from "@/lib/utils";

type RecipeStatus = "draft" | "submitted";

const AUTOSAVE_DELAY_MS = 1500;

const formatRelativeTime = (timestamp: number): string => {
  const seconds = Math.max(0, Math.round((Date.now() - timestamp) / 1000));
  if (seconds < 10) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
};

interface IngredientRow {
  id: string;
  foodId: number;
  foodName: string;
  servingId: number;
  servingLabel: string;
  servingMultiplier: number;
  // Gram weight of one serving unit; row weight = quantity * servingWeightG.
  servingWeightG: number;
  // Stated by the API rather than inferred from servingWeightG, which is
  // coerced to 0 and so can't be told apart from a genuine zero.
  requiresWholeServings: boolean;
  quantity: number;
  // Cooking retention as a fraction in [0,1]. The backend applies it when
  // computing canonical recipe nutrients.
  retentionFactor: number;
}

// One display row in the Nutrient Profile tables: label + already-resolved
// amount ("—" when the figure is null) + native unit. Both are rendered
// verbatim — no lookup, no aliasing, no client-side unit conversion.
interface NutrientDisplayRow {
  key: string;
  label: string;
  amount: string;
  unit: string;
}

interface NutrientDisplayGroup {
  title: string;
  rows: NutrientDisplayRow[];
}

// Compact, read-only nutrient table — mirrors ReadOnlyNutrientTable in
// ViewFood. Rows arrive pre-resolved from the backend nutrient profile, so this
// component does no field lookup or unit math; it just renders them.
//
// Groups open COLLAPSED: the catalog runs to a few hundred rows across every
// group, so an all-expanded tab buries the handful a reviewer actually wants.
const ReadOnlyNutrientTable = ({ title, rows }: NutrientDisplayGroup) => {
  const [open, setOpen] = useState(false);
  const normalizeUnit = (unit: string) => unit.replace(/Î¼|μ/g, "µ");

  if (rows.length === 0) return null;

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="h-fit overflow-hidden rounded-lg border border-border bg-card"
    >
      <CollapsibleTrigger
        className={cn(
          "flex w-full items-center justify-between gap-2 bg-muted/50 px-3 py-2 text-left transition-colors hover:bg-muted",
          open && "border-b border-border"
        )}
      >
        <h4 className="z-label text-foreground">{title}</h4>
        <ChevronDown
          className={cn(
            "size-4 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180"
          )}
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <Table>
          <TableHeader>
            <TableRow className="border-border/60 hover:bg-transparent">
              <TableHead className="z-eyebrow text-muted-foreground h-8 px-3">
                Nutrient
              </TableHead>
              <TableHead className="z-eyebrow text-muted-foreground h-8 px-3 text-right">
                Amount
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const unit = normalizeUnit(row.unit);
              return (
                <TableRow
                  key={row.key}
                  className="border-border/60 hover:bg-muted/20"
                >
                  <TableCell
                    className="px-3 py-2 z-caption text-foreground"
                    title={row.label}
                  >
                    {row.label}
                  </TableCell>
                  <TableCell className="px-3 py-2">
                    <div className="ml-auto flex w-32 items-center justify-end gap-2 z-caption tabular-nums">
                      <span>{row.amount}</span>
                      {unit && (
                        <span className="z-hint text-muted-foreground">
                          {unit}
                        </span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CollapsibleContent>
    </Collapsible>
  );
};

const DIET_FLAG_CODE_TO_KEY: Record<string, DietFlagKey> = {
  vegetarian: "vegetarian",
  vegan: "vegan",
  dairy_free: "dairyFree",
  gluten_free: "glutenFree",
  egg_free: "eggFree",
  jain: "jain",
  satvik: "satvik",
};

// Until the backend has resolved a claim, nothing is known either way.
const defaultDietFlagStates = (): Record<DietFlagKey, DietFlagState> => ({
  vegetarian: { status: "unknown" },
  vegan: { status: "unknown" },
  dairyFree: { status: "unknown" },
  glutenFree: { status: "unknown" },
  eggFree: { status: "unknown" },
  jain: { status: "unknown" },
  satvik: { status: "unknown" },
});

// Only a tag can break a claim; nothing confirms one. Older backends send
// `value: true` when no tag breaks it — that is unknown, never a pass.
const dietFlagStatus = (flag: RecipeDietFlag): DietFlagStatus =>
  flag.status ?? (flag.value === false ? "fail" : "unknown");

// Resolve backend diet flags into display state, naming ingredients by id.
const toDietFlagStates = (
  flags: RecipeDietFlag[] | undefined,
  nameForFoodId: (foodId: number) => string
): Record<DietFlagKey, DietFlagState> => {
  const states = defaultDietFlagStates();
  for (const flag of flags ?? []) {
    const key = DIET_FLAG_CODE_TO_KEY[flag.code];
    if (!key) continue;
    const brokenIds =
      flag.broken_by_ingredient_ids ??
      (flag.broken_by_ingredient_id ? [flag.broken_by_ingredient_id] : []);
    states[key] = {
      status: dietFlagStatus(flag),
      brokenBy: [
        ...brokenIds.map(nameForFoodId),
        ...(flag.broken_by_manual ? ["a hand-added allergen"] : []),
      ],
    };
  }
  return states;
};

const NUTRIENT_FIELD_ALIASES: Record<string, string[]> = {
  energy_kcal: ["energy"],
  energy_kj: ["energyKj"],
  protein_g: ["protein", "proteinG"],
  carbohydrate_g: ["carbohydrate", "carbohydrateG", "carbs"],
  ash_g: ["ash", "ashG"],
  total_sugars_g: ["totalSugar", "totalSugars", "sugar", "sugars"],
  added_sugars_g: ["sugarAdded", "addedSugar", "addedSugars"],
  // Free sugar is its own label row and its own nutrient, not a component of
  // total sugars (Columbus #402): what a food naturally contains, vs what a
  // manufacturer added. IFCT-sourced foods record free sugar and no total
  // sugar, so for those recipes `total_sugars_g` is null and the figure comes
  // through here. Without this alias the catalog's Free Sugar row renders "—"
  // and the recipe looks like it lost its sugar figure entirely.
  free_sugars_g: ["freeSugar", "freeSugars"],
  dietary_fibre_g: [
    "fiber",
    "fibre",
    "fiberTotal",
    "dietaryFiber",
    "dietaryFibre",
  ],
  total_fat_g: ["fat", "totalFat", "totalFatG"],
  saturated_fat_g: ["totalSaturatedFat", "saturatedFat"],
  trans_fat_g: ["transFat", "transFattyAcids"],
  monounsaturated_fat_g: ["totalMonounsaturatedFat", "monounsaturatedFat"],
  polyunsaturated_fat_g: ["totalPolyunsaturatedFat", "polyunsaturatedFat"],
  cholesterol_mg: ["cholesterol"],
  sodium_mg: ["sodium"],
  potassium_mg: ["potassium"],
  water_g: ["water", "waterG"],
  alcohol_g: ["alcohol", "alcoholG"],
  caffeine_mg: ["caffeine", "caffeineMg"],
};

const nutrientKeyToFieldName = (key: string): string => {
  if (key === "energy_kcal") return "energyKcal";
  return key
    .toLowerCase()
    .split(/[-_]/)
    .map((part, index) =>
      index === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)
    )
    .join("");
};

const numericString = (value: number | string | null | undefined) =>
  value === null || value === undefined || Number.isNaN(Number(value))
    ? ""
    : String(value);

const tagIds = (tags: RecipeResolvedTag[] = []) => tags.map((tag) => tag.id);

const lockedTagIds = (tags: RecipeResolvedTag[] = []) =>
  tags
    .filter((tag) => tag.locked || tag.source_food_item_id)
    .map((tag) => tag.id);

const tagOrigins = (tags: RecipeResolvedTag[] = []) =>
  tags.reduce<Record<number, string>>((acc, tag) => {
    if (tag.source_food_item_id) {
      acc[tag.id] = `from food item #${tag.source_food_item_id}`;
    } else if (tag.origin) {
      acc[tag.id] = tag.origin === "manual" ? "added manually" : tag.origin;
    }
    return acc;
  }, {});

const normalizeLabelRows = (
  rows: RecipeDetail["label_rows"]
): FSSAILabelRow[] =>
  rows.map((row) => ({
    ...row,
    unit: row.unit,
  }));

// Which basis the Nutrient Profile tab reads. The label's own per-100g column
// comes from `label_rows`, computed separately server-side — this only drives
// the nutrient tables.
type NutrientBasis = "perServe" | "per100g" | "perRecipe";

// Recipe basis toggle → the matching `nutrient_profile` key.
const PROFILE_BASIS_KEY = {
  perServe: "per_serve",
  per100g: "per_100g",
  perRecipe: "per_recipe",
} as const satisfies Record<NutrientBasis, keyof RecipeNutrientProfile>;

// Clinical group code → section heading. Rows are pre-sorted server-side, so
// sections render in order of first appearance (GEN, CARB, LIP, PROT, VIT, MIN).
const NUTRIENT_GROUP_LABELS: Record<string, string> = {
  GEN: "General",
  CARB: "Carbohydrate",
  LIP: "Fat",
  PROT: "Protein",
  VIT: "Vitamin",
  MIN: "Minerals",
};

// Bucket the pre-sorted nutrient_profile rows into display groups by `group`
// code, preserving first-appearance order (no re-sorting). Amount null → "—"
// via `?? ` so a computed 0 survives and still reads as "0"; unit is native, so
// amount and unit both render verbatim with no client-side conversion.
const buildProfileGroups = (
  rows: RecipeNutrientProfileRow[]
): NutrientDisplayGroup[] => {
  const order: string[] = [];
  const byGroup = new Map<string, NutrientDisplayRow[]>();
  for (const row of rows) {
    if (!byGroup.has(row.group)) {
      byGroup.set(row.group, []);
      order.push(row.group);
    }
    byGroup.get(row.group)!.push({
      key: row.nutrient_slug,
      label: row.nutrient_name,
      amount: String(row.amount ?? "—"),
      unit: row.unit,
    });
  }
  return order.map((code) => ({
    title: NUTRIENT_GROUP_LABELS[code] ?? code,
    rows: byGroup.get(code)!,
  }));
};

// Which tab each server publish-blocker lives on, so we can badge the tab and
// highlight the field. Unknown codes still count in the Review & Publish total.
const BLOCKER_TAB: Record<
  string,
  "info" | "ingredients" | "servings" | "classification"
> = {
  MISSING_NAME: "info",
  DUPLICATE_FOOD_NAME: "info",
  NO_INGREDIENTS: "ingredients",
  MISSING_INGREDIENT_WEIGHT: "ingredients",
  // Both of these still fire on the deployed build. columbus#404 removes them —
  // publish writes the three built-in serving rows itself, so they have nothing
  // left to guard — but that is not merged. Routing a code the server never
  // sends is harmless, so these stay through the transition and can be deleted
  // once #404 is live everywhere.
  NO_SERVINGS: "servings",
  MISSING_DEFAULT_SERVING_WEIGHT: "servings",
  MISSING_YIELD_WEIGHT: "servings",
  MISSING_MEAL_TYPE: "classification",
  MISSING_TEXTURE: "classification",
};

// The core row whose weight the FSSAI label quotes. Confirmed against
// `_build_core_serving_rows`: the complete set of server-built kinds is
// "core-full", "core-serving" and "core-gram", all hyphenated and lowercase;
// authored rows come back as "manual". The earlier `__core_serving__` spelling
// was never a wire value and is gone.
const CORE_SERVING_KIND = "core-serving";

// Small amber count shown in a tab title, mirroring the Review & Publish badge.
const TabCount = ({ n }: { n: number }) =>
  n > 0 ? (
    <span className="ml-1.5 inline-flex min-w-[1.125rem] items-center justify-center rounded-full bg-warning/20 px-1 text-warning z-hint tabular-nums">
      {n}
    </span>
  ) : null;

export const AddRecipeForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // The recipe list passes its search/filters along, so leaving the recipe
  // returns to the same results rather than the full list.
  const recipeListPath = `/settings/nutrition/view-recipes${
    (location.state as { recipeListSearch?: string } | null)
      ?.recipeListSearch ?? ""
  }`;
  const queryClient = useQueryClient();

  // The form is id-driven: `?id=` loads an existing recipe (view/edit), its
  // absence creates a new one. The state below seeds from `loaded` so opening a
  // submitted recipe shows the saved record read-only — not a blank form.
  // Existing recipes hydrate from GET /recipes/:id; new recipes are created
  // on open and then hydrate from the returned recipe payload.
  // render a skeleton instead of the empty initial state.
  const [searchParams, setSearchParams] = useSearchParams();
  const recipeId = searchParams.get("id");
  const [currentRecipeId, setCurrentRecipeId] = useState<string | null>(
    () => recipeId
  );
  const idempotencyKey = useRef(crypto.randomUUID());
  const [submitIdempotencyKey, setSubmitIdempotencyKey] = useState(() =>
    crypto.randomUUID()
  );
  const createStarted = useRef(false);
  // Create-on-open is attempted ONCE. Clearing the guard inside the failure
  // handler re-arms the effect on the very next render (useMutation hands back
  // a fresh object each render, so the effect's deps always change), which
  // turns one failing POST /recipes into an unbounded request storm against
  // the backend. A failure parks here and waits for an explicit retry.
  const [createError, setCreateError] = useState<string | null>(null);
  const [createAttempt, setCreateAttempt] = useState(0);
  const createRecipe = useCreateRecipe();
  const updateRecipe = useUpdateRecipe();
  const submitRecipe = useSubmitRecipe();
  const deleteRecipe = useDeleteRecipe();
  const discardRecipe = useDiscardRecipe();
  const recipeQuery = useRecipe(currentRecipeId);
  const recipe = recipeQuery.data;
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  // Type-to-confirm text for the draft-delete dialog.
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  // Confirmation prompt shown before creating a new version of a published recipe.
  const [newVersionDialogOpen, setNewVersionDialogOpen] = useState(false);
  // Confirmation shown before publishing staged edits made by another clinician.
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  // Confirmation shown before discarding a staged draft (revert to published).
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);

  const [activeTab, setActiveTab] = useState<
    | "info"
    | "ingredients"
    | "classification"
    | "nutrients"
    | "servings"
    | "label"
    | "publish"
  >("info");

  // A recipe is created as a draft the moment the form opens so the user always
  // has a resumable server artifact.
  const [status, setStatus] = useState<RecipeStatus>("draft");
  // A submitted recipe opens locked (read-only). The user must click "Edit
  // recipe" to unlock the fields — mirrors ViewFood's Pencil-edit pattern so a
  // published recipe is never mutated by accident. Drafts are always editable.
  const [isEditing, setIsEditing] = useState(false);
  // Version is an opaque optimistic-concurrency token. The FE only round-trips
  // it in If-Match.
  const [version, setVersion] = useState<number>(1);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(Date.now());
  const [isSaving, setIsSaving] = useState(false);
  // BE-assigned food code. null until the BE hands one back.
  const [foodCode, setFoodCode] = useState<string | null>(null);
  const [blockerOverride, setBlockerOverride] = useState<
    RecipeBlocker[] | null
  >(null);
  const [hydratedRecipeKey, setHydratedRecipeKey] = useState<string | null>(
    null
  );
  // Tracks the last recipe id we hydrated so we only re-lock (exit edit mode)
  // when a *different* recipe loads — not on every same-recipe autosave/stage.
  const lastHydratedIdRef = useRef<string | null>(null);

  // Minted once on mount; sent with POST /recipes (create) and
  // POST /recipes/:id/submit so retries never duplicate (contract §5.0).
  // Info — seeded from the loaded record (summary fields only today; the rest
  // hydrate from the real recipe detail payload.
  const [recipeName, setRecipeName] = useState("");
  const [hindiName, setHindiName] = useState("");
  const [source, setSource] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [sourceConfidence, setSourceConfidence] = useState("");
  const [brand, setBrand] = useState("");
  const [favourite, setFavourite] = useState(false);
  const [homemade, setHomemade] = useState(true);
  const [notes, setNotes] = useState("");

  // Per-field edit-lock (new-version mode): which fields the clinician has
  // unlocked, and a snapshot of the values when the recipe was opened (the
  // "previous entry") so we can flag edits and offer a Reset.
  const [editableFields, setEditableFields] = useState<Set<string>>(new Set());
  const baselineValuesRef = useRef<Record<string, unknown>>({});

  // Ingredients
  const [ingredients, setIngredients] = useState<IngredientRow[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Classification
  const [cuisineTags, setCuisineTags] = useState<number[]>([]);
  const [feedingCategories, setFeedingCategories] = useState<number[]>([]);
  const [mealTypes, setMealTypes] = useState<number[]>([]);
  const [conditionTags, setConditionTags] = useState<number[]>([]);
  const [allergenTags, setAllergenTags] = useState<number[]>([]);
  const [ageSuitabilityTags, setAgeSuitabilityTags] = useState<number[]>([]);
  // What the clinician narrowed age suitability to. Empty means "no
  // narrowing": the recipe takes every band its ingredients allow. Kept apart
  // from the resolved tags so an untouched recipe never pins today's bands.
  const [ageIntent, setAgeIntent] = useState<number[]>([]);
  const [contextTags, setContextTags] = useState<number[]>([]);
  const [texture, setTexture] = useState("");

  // Servings. Backend-returned serving rows and weights are the source of truth.
  const [servingMode, setServingMode] = useState<ServingMode>("serving");
  const [servingsPerRecipe, setServingsPerRecipe] = useState("1");
  // Extra named servings (e.g. "1 Bowl") on top of the three core rows.
  const [recipeServings, setRecipeServings] = useState<RecipeServing[]>([]);

  const { toast } = useToast();
  const { data: tagMetadata, isLoading: isLoadingMetadata } = useTagMetadata();

  // Nutrient catalog reused from the Add Food Entry form so both forms
  // list the exact same nutrient rows.
  const { catalog: nutrientCatalog } = useNutrientCatalog();
  const foodSources = tagMetadata?.sources_and_brands.food_sources ?? [];
  const foodBrands = tagMetadata?.sources_and_brands.food_brands ?? [];
  const sourceConfidenceOptions = tagMetadata?.choices.source_confidence ?? [];
  const textureOptions = tagMetadata?.choices.texture ?? [];

  useEffect(() => {
    setCurrentRecipeId(recipeId);
  }, [recipeId]);

  useEffect(() => {
    if (currentRecipeId || createStarted.current) return;
    createStarted.current = true;
    createRecipe
      .mutateAsync({ idempotency_key: idempotencyKey.current })
      .then((created) => {
        setCurrentRecipeId(created.id);
        setSearchParams({ id: created.id }, { replace: true });
      })
      .catch((error: any) => {
        setCreateError(error?.message || "Please try again.");
        toast({
          title: "Could not create recipe",
          description: error?.message || "Try again.",
          variant: "destructive",
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRecipeId, createAttempt, setSearchParams, toast]);

  // Retry keeps the same idempotency key — that is the point of the key: the
  // failed attempt either never landed or is replayed onto the same record.
  const retryCreateRecipe = () => {
    createStarted.current = false;
    setCreateError(null);
    setCreateAttempt((n) => n + 1);
  };

  const hydrateRecipe = (nextRecipe: RecipeDetail) => {
    const nextHydrationKey = `${nextRecipe.id}:${nextRecipe.version}`;
    setBlockerOverride(null);
    setStatus(nextRecipe.status);
    setVersion(nextRecipe.version);
    setFoodCode(nextRecipe.food_code ?? null);
    setRecipeName(nextRecipe.name ?? "");
    setHindiName(nextRecipe.hindi_name ?? "");
    setSource(nextRecipe.source ?? "");
    setSourceUrl(nextRecipe.source_url ?? "");
    setSourceConfidence(nextRecipe.source_confidence ?? "");
    setBrand(nextRecipe.brand ?? "");
    setFavourite(Boolean(nextRecipe.is_favorite));
    setHomemade(nextRecipe.is_homemade ?? true);
    setNotes(nextRecipe.notes ?? "");
    setIngredients(
      (nextRecipe.ingredients ?? []).map((row) => ({
        id: row.id,
        foodId: row.food_item_id,
        foodName: row.food_name,
        servingId: row.serving_id,
        servingLabel: row.serving_label,
        servingMultiplier: Number(row.serving_multiplier) || 1,
        servingWeightG: Number(row.serving_weight_g) || 0,
        requiresWholeServings: Boolean(row.requires_whole_servings),
        quantity: Number(row.quantity) || 0,
        // 0 is a real retention (the row's nutrients are fully lost to cooking)
        // and the input accepts it, so it must survive the round-trip. Only a
        // missing or unparseable value falls back to 1.
        retentionFactor:
          row.retention_factor == null ||
          !Number.isFinite(Number(row.retention_factor))
            ? 1
            : Number(row.retention_factor),
      }))
    );
    setServingMode(nextRecipe.serving_mode ?? "serving");
    setServingsPerRecipe(
      String(
        nextRecipe.servings?.servings_per_recipe ??
          nextRecipe.servings_per_recipe ??
          1
      )
    );
    setRecipeServings(
      (nextRecipe.servings?.serving_rows ?? []).map((row) => ({
        id: row.id,
        kind: "manual",
        servingUnitCode: row.serving_unit_code,
        servingUnitName: row.serving_unit_name,
        description: row.description,
        weightG: numericString(row.weight_g),
      }))
    );
    const tagInfo = nextRecipe.tag_info ?? {};
    setCuisineTags(tagInfo.cuisine_tag_ids ?? []);
    setFeedingCategories(tagInfo.feeding_category_ids ?? []);
    setMealTypes(tagInfo.meal_type_ids ?? []);
    setConditionTags(tagInfo.condition_tag_ids ?? []);
    setAllergenTags(tagInfo.allergen_tag_ids ?? []);
    setAgeSuitabilityTags(tagInfo.age_suitability_tag_ids ?? []);
    setAgeIntent(tagInfo.age_suitability_tag_intent_ids ?? []);
    setContextTags(tagInfo.context_tag_ids ?? []);
    setTexture(tagInfo.texture ?? nextRecipe.tag_info?.texture ?? "");
    // A published recipe opens READ-ONLY. If it has a staged draft, the header
    // shows a "Draft in progress" pill that routes into the draft via a dialog.
    // Re-hydrating the same recipe (e.g. after an autosave/stage) must NOT flip
    // the mode mid-flow, nor reset the per-field locks/baseline.
    if (lastHydratedIdRef.current !== nextRecipe.id) {
      setIsEditing(false);
      setEditableFields(new Set());
      baselineValuesRef.current = {
        name: nextRecipe.name ?? "",
        hindiName: nextRecipe.hindi_name ?? "",
        source: nextRecipe.source ?? "",
        sourceUrl: nextRecipe.source_url ?? "",
        sourceConfidence: nextRecipe.source_confidence ?? "",
        brand: nextRecipe.brand ?? "",
        favourite: Boolean(nextRecipe.is_favorite),
        homemade: nextRecipe.is_homemade ?? true,
        notes: nextRecipe.notes ?? "",
      };
    }
    lastHydratedIdRef.current = nextRecipe.id;
    setLastSavedAt(Date.now());
    setHydratedRecipeKey(nextHydrationKey);
  };

  useEffect(() => {
    if (!recipe) return;
    hydrateRecipe(recipe);
  }, [recipe]);

  // "Fasting Safe" is worked out from the ingredients: the recipe carries it
  // only when every ingredient does, so it is locked when present and never
  // offered for picking by hand.
  const inheritedContextIds = (recipe?.derived_tags?.context_tags ?? [])
    .filter((tag) => tag.origin === "ingredient")
    .map((tag) => tag.id);
  const isFastingSafeTag = (tag: { slug?: string; name: string }) =>
    (tag.slug ?? tag.name).toLowerCase().replace(/[^a-z0-9]/g, "") ===
    "fastingsafe";

  const classificationRows = [
    {
      label: "Cuisine Tags",
      options: tagMetadata?.classification_tags.cuisine_tags ?? [],
      selectedIds: cuisineTags,
      onChange: setCuisineTags,
    },
    {
      label: "Feeding Method",
      options: tagMetadata?.classification_tags.feeding_categories ?? [],
      selectedIds: feedingCategories,
      onChange: setFeedingCategories,
    },
    {
      label: "Meal Type",
      options: tagMetadata?.classification_tags.meal_types ?? [],
      selectedIds: mealTypes,
      onChange: setMealTypes,
    },
    {
      label: "Condition Suitability",
      options: tagMetadata?.classification_tags.condition_tags ?? [],
      selectedIds: conditionTags,
      onChange: setConditionTags,
    },
    {
      label: "Allergen Tags",
      options: tagMetadata?.classification_tags.allergen_tags ?? [],
      selectedIds: allergenTags,
      onChange: setAllergenTags,
    },
    {
      label: "Age Suitability",
      // Only the bands every ingredient allows can be picked: age suitability
      // narrows, never broadens.
      options:
        recipe?.derived_tags?.age_suitability_options ??
        tagMetadata?.classification_tags.age_suitability_tags ??
        [],
      selectedIds: ageSuitabilityTags,
      onChange: (ids: number[]) => {
        setAgeSuitabilityTags(ids);
        setAgeIntent(ids);
      },
    },
    {
      label: "Context Tags",
      options: (tagMetadata?.classification_tags.context_tags ?? []).filter(
        (tag) => !isFastingSafeTag(tag) || inheritedContextIds.includes(tag.id)
      ),
      selectedIds: contextTags,
      onChange: setContextTags,
    },
  ];

  const fullWeightG = Number(recipe?.total_weight_g) || 0;
  const hasWeight = fullWeightG > 0;

  // Yield is typed: how many servings the recipe makes (min 1).
  const servingsCount = Math.max(Math.round(Number(servingsPerRecipe) || 1), 1);

  // A recipe whose food exists. Its servings belong to the food, so the tab is
  // read-only and no save carries serving rows. "submitted" is the published
  // state; food_version_number covers a published recipe reopened as a draft.
  const isPublishedRecipe =
    status === "submitted" || recipe?.food_version_number != null;
  const foodItemId = recipe?.food_item_id ?? null;
  const canManageServings =
    foodItemId != null && recipe?.capabilities?.can_manage_servings !== false;
  // Opens the food page in a new tab so the recipe form (and any unsaved edits)
  // stay put; the window-focus refetch below picks up what changed there.
  const openFoodServings = () => {
    if (foodItemId == null) return;
    window.open(
      `/settings/nutrition/view-food/${foodItemId}`,
      "_blank",
      "noopener"
    );
  };

  // Servings on the recipe's food: shown read-only, never sent back as recipe
  // rows (the food page owns them).
  const foodServingRows = useMemo<RecipeServing[]>(
    () =>
      (recipe?.servings?.food_rows ?? []).map((row) => ({
        id: row.id,
        kind: "food",
        servingUnitCode: row.serving_unit_code,
        servingUnitName: row.serving_unit_name,
        description: row.description,
        weightG: numericString(row.weight_g),
      })),
    [recipe?.servings?.food_rows]
  );

  // Core read-only rows come from the backend; the FE only renders them.
  const coreServingRows = useMemo<RecipeServing[]>(
    () =>
      (recipe?.servings?.core_rows ?? []).map((row) => ({
        id: row.id,
        kind: (row.kind ?? "manual") as RecipeServing["kind"],
        servingUnitCode: row.serving_unit_code,
        servingUnitName: row.serving_unit_name,
        description: row.description,
        weightG: numericString(row.weight_g),
        servingsPerRecipe: numericString(row.servings_per_recipe),
      })),
    [recipe?.servings?.core_rows]
  );

  // One serving is the canonical portion for the FSSAI label, and its weight is
  // the CORE serving row's weight_g — total weight / servings_per_recipe, the
  // exact amount the nutrient numbers describe (Columbus #404). Deliberately
  // NOT the default `serving_rows` row: that is an authored portion which can
  // disagree with the nutrients. A 420 g recipe of 2 servings with an authored
  // "Bar" row of 234 g would print "1 serving (234 g)" beside nutrition for
  // 210 g. The core row cannot drift, because it is derived from the same two
  // numbers the per-serve basis is.
  //
  // A draft with no computed core row yet (no ingredients, so no total weight)
  // renders the bare portion rather than claiming a "(0 g)" serving.
  const defaultServingLabel = useMemo(() => {
    const coreRows = recipe?.servings?.core_rows ?? [];
    const weightG =
      coreRows.find((row) => row.kind === CORE_SERVING_KIND)?.weight_g ?? 0;
    return weightG > 0 ? `1 serving (${Math.round(weightG)} g)` : "1 serving";
  }, [recipe?.servings?.core_rows]);

  // ── Nutrient basis ──────────────────────────────────────────────────
  // The server computes all three bases; the tab shows one at a time rather
  // than leaving the reader to guess which one the numbers are on.
  const [nutrientBasis, setNutrientBasis] = useState<NutrientBasis>("perServe");

  // Name the basis in full rather than just "Per serving" — a serving is only
  // meaningful next to its weight, and the whole-recipe figure next to the
  // yield weight the numbers were actually summed over.
  const nutrientBasisHelper =
    nutrientBasis === "per100g"
      ? "Values per 100 g of the finished recipe."
      : nutrientBasis === "perRecipe"
        ? `Values for the full recipe${
            hasWeight ? ` (${Math.round(fullWeightG)} g)` : ""
          }.`
        : `Values for ${defaultServingLabel}.`;

  // ── Nutrient table groups (shared shape with Add Food Entry) ─────────
  // Same `BASIC_TOP_LEVEL_FIELDS` slot order as the food form so reviewers
  // see a familiar table. Values are read-only here — they'll hydrate
  // from the BE once `nutrients_per_serve` is populated server-side from
  // the ingredient sums.
  const nutrientGroups = useMemo(() => {
    const cats = nutrientCatalog?.categories ?? [];
    const general = cats.find((c) => c.title === "General");
    return [
      ...(general
        ? [
            {
              title: general.title,
              fields: general.fields as EditableNutrientField[],
            },
          ]
        : []),
      ...cats
        .filter((c) => c.title !== "General")
        .map((c) => ({
          title: c.title,
          fields: c.fields as EditableNutrientField[],
        })),
    ];
  }, [nutrientCatalog]);

  // Which basis the nutrient tables read. The server sends all three with an
  // identical key set, so switching never adds or drops a row.
  const nutrientBasisValues =
    nutrientBasis === "per100g"
      ? recipe?.nutrients_per_100g
      : nutrientBasis === "perRecipe"
        ? recipe?.nutrients_per_recipe
        : recipe?.nutrients_per_serve;

  // Backend-computed nutrients arrive in contract keys (e.g. protein_g);
  // fill the matching catalog field aliases so the table remains render-only.
  //
  // numericString maps null → "" (rendered as "—") and 0 → "0", which is the
  // distinction the contract draws: null means no ingredient supplied the
  // nutrient, 0 means it was computed and is genuinely zero. A whole basis
  // arrives all-null when it isn't computable (per_100g with no total weight,
  // per_serve with no servings), which correctly reads as "—" throughout.
  const nutrientValues: Record<string, string> = useMemo(() => {
    const values: Record<string, string> = {};
    const catalogFieldNames = new Set(
      nutrientGroups.flatMap((group) => group.fields.map((field) => field.name))
    );
    for (const [key, value] of Object.entries(nutrientBasisValues ?? {})) {
      const displayValue = numericString(value);
      const fieldName = [
        nutrientCatalog?.formFieldBySlug[key],
        nutrientKeyToFieldName(key),
        ...(NUTRIENT_FIELD_ALIASES[key] ?? []),
      ].find((candidate): candidate is string =>
        Boolean(candidate && catalogFieldNames.has(candidate))
      );
      if (fieldName) values[fieldName] = displayValue;
    }
    return values;
  }, [nutrientCatalog?.formFieldBySlug, nutrientGroups, nutrientBasisValues]);

  // What the Nutrient Profile tab renders. Prefer the backend `nutrient_profile`
  // — the COMPLETE nutrient set (every amino acid, vitamin, mineral) in native
  // units, already sectioned by clinical group. Fall back to the label-keyed
  // catalog tables (only ~15 nutrients) until that field deploys, so the tab
  // keeps working whichever ships first.
  const displayGroups = useMemo<NutrientDisplayGroup[]>(() => {
    const profile = recipe?.nutrient_profile;
    if (profile) {
      return buildProfileGroups(profile[PROFILE_BASIS_KEY[nutrientBasis]]);
    }
    return nutrientGroups.map((group) => ({
      title: group.title,
      rows: group.fields.map((field) => {
        const value = nutrientValues[field.name];
        return {
          key: field.name,
          label: field.label,
          amount: value === undefined || value === "" ? "—" : value,
          unit: field.unit,
        };
      }),
    }));
  }, [recipe?.nutrient_profile, nutrientBasis, nutrientGroups, nutrientValues]);

  // ── Tag propagation from ingredients ────────────────────────────────
  // Resolved tags come from the backend recipe payload.
  const derivedAllergenTags = useMemo(
    () => recipe?.derived_tags?.allergen_tags ?? [],
    [recipe?.derived_tags?.allergen_tags]
  );
  const derivedAgeTags = useMemo(
    () => recipe?.derived_tags?.age_suitability_tags ?? [],
    [recipe?.derived_tags?.age_suitability_tags]
  );

  // Diet flags are rendered from the backend resolved recipe payload.
  const dietFlagStates = useMemo(() => {
    const ingredientNameById = new Map(
      ingredients.map((ingredient) => [ingredient.foodId, ingredient.foodName])
    );
    return toDietFlagStates(
      recipe?.diet_flags,
      (foodId) => ingredientNameById.get(foodId) ?? `Food item #${foodId}`
    );
  }, [ingredients, recipe?.diet_flags]);

  // Each ingredient's own classification, keyed by food so a row added since
  // the last save simply shows nothing until the backend has resolved it.
  const ingredientTagsByFoodId = useMemo(
    () =>
      new Map(
        (recipe?.ingredients ?? []).map((row) => [row.food_item_id, row])
      ),
    [recipe?.ingredients]
  );

  // Allergens — union. Convert propagated allergen labels to tag IDs
  // against the live allergen_tags catalog so the lock semantics work.
  const allergenOptions = useMemo(
    () => tagMetadata?.classification_tags.allergen_tags ?? [],
    [tagMetadata]
  );
  const autoAllergen = useMemo(() => {
    return {
      ids: tagIds(derivedAllergenTags),
      lockedIds: lockedTagIds(derivedAllergenTags),
      origin: tagOrigins(derivedAllergenTags),
    };
  }, [derivedAllergenTags]);

  // Age suitability narrows: the backend resolves the bands every ingredient
  // allows and offers only those as options. Nothing is locked — removing a
  // band is narrowing, which is always allowed; widening isn't offered.
  const autoAge = useMemo(
    () => ({
      ids: tagIds(derivedAgeTags),
      origin: tagOrigins(derivedAgeTags),
    }),
    [derivedAgeTags]
  );

  // Condition suitability advisories come from backend warnings.
  const conditionAdvisories = useMemo(() => {
    return (recipe?.warnings ?? []).map((warning: RecipeWarning) => ({
      tagName: warning.code,
      message: warning.message,
    }));
  }, [recipe?.warnings]);

  const handleLockedAllergenRemove = (tagId: number) => {
    const tag = allergenOptions.find((o) => o.id === tagId);
    const origin = autoAllergen.origin[tagId] ?? "from an ingredient";
    toast({
      title: tag ? `“${tag.name}” is inherited` : "Inherited tag",
      description: `This allergen came ${origin}. To remove it from the recipe, change or drop that ingredient — the system won't let you silently strip a safety-critical claim.`,
      variant: "destructive",
      action: (
        <ToastAction
          altText="Go to ingredients"
          onClick={() => setActiveTab("ingredients")}
        >
          Change ingredient
        </ToastAction>
      ),
    });
  };

  const handleInheritedContextRemove = () => {
    toast({
      title: "Worked out from the ingredients",
      description:
        "This recipe is fasting safe because every ingredient is. It goes away by itself if an ingredient that isn't fasting safe is added.",
    });
  };

  const handlePickerAdd = async (payload: PickedFoodPayload) => {
    if (!currentRecipeId || !canSave || !hasHydratedCurrentRecipe || isSaving) {
      toast({
        title: "Could not add ingredient",
        description: "Wait for the recipe to finish saving, then try again.",
        variant: "destructive",
      });
      throw new Error("Recipe is not ready to add an ingredient.");
    }
    const nextIngredients: IngredientRow[] = [
      ...ingredients,
      {
        id: crypto.randomUUID(),
        foodId: payload.foodId,
        foodName: payload.foodName,
        servingId: payload.servingId,
        servingLabel: payload.servingLabel,
        servingMultiplier: payload.servingMultiplier,
        servingWeightG: payload.servingWeightG,
        requiresWholeServings: payload.requiresWholeServings,
        quantity: payload.quantity,
        retentionFactor: 1,
      },
    ];
    setIsSaving(true);
    try {
      const saved = await updateRecipe.mutateAsync({
        id: currentRecipeId,
        version,
        payload: buildWritePayload({ ingredients: nextIngredients }),
      });
      hydrateRecipe(saved);
      setLastSavedAt(Date.now());
      setBlockerOverride(saved.blockers ?? null);
    } catch (error) {
      try {
        await handleRecipeConflict(error);
        throw error;
      } catch (addError: any) {
        toast({
          title: "Could not add ingredient",
          description: addError?.message || "Try again.",
          variant: "destructive",
        });
        throw addError;
      }
    } finally {
      setIsSaving(false);
    }
  };

  const updateRow = (id: string, patch: Partial<IngredientRow>) =>
    setIngredients((rows) =>
      rows.map((r) => (r.id === id ? { ...r, ...patch } : r))
    );

  const removeRow = (id: string) =>
    setIngredients((rows) => rows.filter((r) => r.id !== id));

  const canSave =
    recipe?.capabilities?.can_edit === true && Boolean(currentRecipeId);
  const canDelete =
    recipe?.capabilities?.can_delete === true && Boolean(currentRecipeId);
  // Duplicate-name (and every other gate) now comes from the server as a
  // blocker (DUPLICATE_FOOD_NAME) with can_submit:false — rendered here and
  // used for publish gating. No client-side name lookup.
  const submitBlockers = useMemo(
    () => blockerOverride ?? recipe?.blockers ?? [],
    [blockerOverride, recipe?.blockers]
  );

  // Draft→publish model (Columbus #392): the recipe is the draft workspace.
  // Editing a published recipe stages the changes (has_unpublished_changes)
  // without touching the live food; "Publish" (re-POST submit) snapshots a new
  // immutable food version and retires the previous one. food_code is stable
  // across versions. Show Publish whenever there are staged changes or the
  // recipe is first-time publishable; can_submit reflects server blockers.
  const hasUnpublishedChanges = recipe?.has_unpublished_changes ?? false;
  const canSubmit = recipe?.capabilities?.can_submit === true;
  const canPublish = canSubmit || hasUnpublishedChanges;

  // Save persists any draft state (no requirements). The publish gate is now
  // fully server-driven (Columbus #392): blockers[] covers name, ingredients,
  // serving rows, yield weight, meal type, and texture, with can_submit:false
  // until they're all met. We just render the messages — no client duplication.
  // columbus#404 drops the two serving-row blockers, but it is not deployed.
  const publishIssues = submitBlockers.map((b) => b.message);
  // Route blockers to their tab (for tab count badges) and flag the specific
  // fields (to highlight them where they live).
  const tabBlockerCounts = useMemo(() => {
    const counts = {
      info: 0,
      ingredients: 0,
      servings: 0,
      classification: 0,
    };
    for (const blocker of submitBlockers) {
      const tab = BLOCKER_TAB[blocker.code];
      if (tab) counts[tab] += 1;
    }
    return counts;
  }, [submitBlockers]);
  const blockerCodes = useMemo(
    () => new Set(submitBlockers.map((b) => b.code)),
    [submitBlockers]
  );
  const mealTypeMissing = blockerCodes.has("MISSING_MEAL_TYPE");
  const textureMissing = blockerCodes.has("MISSING_TEXTURE");

  // Recipe drafts are SHARED across console users. If the staged changes were
  // last edited by someone else, warn on the badge and confirm before publish.
  const currentUserId = useAuthStore((s) => s.user?.id);
  const draftByOther =
    hasUnpublishedChanges &&
    Boolean(recipe?.draft_updated_by_id) &&
    recipe?.draft_updated_by_id !== currentUserId;
  // The version the DRAFT will become on publish — comes from the backend only
  // once a real draft is staged (draft_target_version_number). We do NOT infer
  // it: a draft is not a version until published, so we never show a number the
  // backend hasn't reconciled. Null → show "New version · draft" without a number.
  const draftTarget = recipe?.draft_target_version_number ?? null;

  const buildWritePayload = (overrides?: {
    ingredients?: IngredientRow[];
    recipeServings?: RecipeServing[];
  }): RecipeWritePayload => {
    const allergenLocked = new Set(autoAllergen.lockedIds);
    const ingredientRows = overrides?.ingredients ?? ingredients;
    const servingRows = overrides?.recipeServings ?? recipeServings;
    // Confirmed with the backend: columbus#404 does not touch the recipe
    // multiplier at all. `RecipeServing.multiplier` is a different column from
    // `FoodServing.multiplier` — decimal(8,3) here against decimal(5,2) there —
    // so the food-serving rules and the 4dp precision do NOT carry over. This
    // stays as it is; do not unify the two.
    const manualServingRows = servingRows.map((row, index) => ({
      serving_unit_code: row.servingUnitCode,
      ...(row.description ? { description: row.description } : {}),
      weight_g: row.weightG,
      multiplier: "1.000",
      is_default: index === 0,
      row_order: index,
    }));
    return {
      name: recipeName,
      hindi_name: hindiName,
      source,
      source_url: sourceUrl,
      source_confidence: sourceConfidence,
      brand,
      is_favorite: favourite,
      is_homemade: homemade,
      notes,
      serving_mode: servingMode,
      servings_per_recipe: servingsCount,
      // cooked_weight_g intentionally omitted: the FE has no UI to drive it and
      // it OVERRIDES the summed ingredient weight when set (brief §6.10).
      // Echoing it back risks corrupting total_weight_g, so we never write it.
      texture,
      ingredients: ingredientRows.map((row, index) => ({
        food_item_id: row.foodId,
        serving_id: row.servingId,
        quantity: row.quantity.toFixed(3),
        retention_factor: row.retentionFactor.toFixed(4),
        row_order: index,
      })),
      // Servings belong to the food once published: never send them then.
      ...(isPublishedRecipe ? {} : { manual_servings: manualServingRows }),
      tag_info: {
        cuisine_tag_ids: cuisineTags,
        feeding_category_ids: feedingCategories,
        meal_type_ids: mealTypes,
        condition_tag_ids: conditionTags,
        context_tag_ids: contextTags,
        allergen_tag_intent_ids: allergenTags.filter(
          (id) => !allergenLocked.has(id)
        ),
        age_suitability_tag_intent_ids: ageIntent,
      },
    };
  };

  // ── Auto-draft ────────────────────────────────────────────────────────
  // Anytime the form changes and the recipe has a name, debounce-save it.
  // Saves hit the real recipe endpoints. Submitted recipes don't auto-save;
  // those go through explicit "Save changes" to make edit intent obvious.
  const formSignature = useMemo(
    () =>
      JSON.stringify({
        recipeName,
        hindiName,
        source,
        sourceUrl,
        sourceConfidence,
        brand,
        favourite,
        homemade,
        notes,
        ingredients,
        cuisineTags,
        feedingCategories,
        mealTypes,
        conditionTags,
        allergenTags,
        ageSuitabilityTags,
        ageIntent,
        contextTags,
        texture,
        servingMode,
        servingsPerRecipe,
        recipeServings,
      }),
    [
      recipeName,
      hindiName,
      source,
      sourceUrl,
      sourceConfidence,
      brand,
      favourite,
      homemade,
      notes,
      ingredients,
      cuisineTags,
      feedingCategories,
      mealTypes,
      conditionTags,
      allergenTags,
      ageSuitabilityTags,
      ageIntent,
      contextTags,
      texture,
      servingMode,
      servingsPerRecipe,
      recipeServings,
    ]
  );

  // Ref to the latest signature so the save handler reads the freshest value.
  const lastSavedSignature = useRef<string | null>(null);
  const baselineRecipeKey = useRef<string | null>(null);
  const currentRecipeKey =
    recipe && currentRecipeId && recipe.id === currentRecipeId
      ? `${recipe.id}:${recipe.version}`
      : null;
  const hasHydratedCurrentRecipe =
    Boolean(currentRecipeKey) && hydratedRecipeKey === currentRecipeKey;

  const rotateSubmitIdempotencyKey = () => {
    setSubmitIdempotencyKey(crypto.randomUUID());
  };

  useEffect(() => {
    if (!currentRecipeKey || !hasHydratedCurrentRecipe) return;
    if (baselineRecipeKey.current === currentRecipeKey) return;
    lastSavedSignature.current = formSignature;
    baselineRecipeKey.current = currentRecipeKey;
  }, [currentRecipeKey, formSignature, hasHydratedCurrentRecipe]);

  const handleRecipeConflict = async (error: unknown) => {
    if (!isRecipeConflictError(error)) throw error;
    if (error.currentRecipe) {
      queryClient.setQueryData(
        queryKeys.nutrition.recipes.detail(error.currentRecipe.id),
        error.currentRecipe
      );
      hydrateRecipe(error.currentRecipe);
    } else if (currentRecipeId) {
      await recipeQuery.refetch();
    }
    toast({
      title: "Recipe updated elsewhere",
      description: "Latest saved version has been loaded.",
      variant: "destructive",
    });
  };

  const performRecipeSave = async (_nextStatus: RecipeStatus) => {
    if (!currentRecipeId || !canSave || !hasHydratedCurrentRecipe || isSaving) {
      return;
    }
    setIsSaving(true);
    try {
      const saved = await updateRecipe.mutateAsync({
        id: currentRecipeId,
        version,
        payload: buildWritePayload(),
      });
      hydrateRecipe(saved);
      lastSavedSignature.current = formSignature;
      setLastSavedAt(Date.now());
      setBlockerOverride(saved.blockers ?? null);
      return saved;
    } catch (error) {
      try {
        await handleRecipeConflict(error);
      } catch (saveError: any) {
        if (isRecipeBlockerError(saveError)) {
          setBlockerOverride(saveError.blockers);
          return;
        }
        toast({
          title: "Could not save recipe",
          description: saveError?.message || "Try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (!hasHydratedCurrentRecipe) return;
    // A published recipe is locked until "Edit recipe"; once editing, autosave
    // STAGES the changes (PATCH sets has_unpublished_changes without touching
    // the live food) — same as a draft. Only skip while read-only.
    if (status === "submitted" && !isEditing) return;
    if (formSignature === lastSavedSignature.current) return;

    const timer = window.setTimeout(
      () => performRecipeSave("draft"),
      AUTOSAVE_DELAY_MS
    );
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formSignature, hasHydratedCurrentRecipe, status, isEditing]);

  // True when the user has typed something the auto-save hasn't yet flushed.
  const hasUnsavedChanges =
    hasHydratedCurrentRecipe &&
    formSignature !== lastSavedSignature.current &&
    !isSaving;
  // Save just flushes the draft — enabled whenever there's something unsaved.
  const saveDisabled = isSaving || !hasUnsavedChanges;
  // Publish is enabled when there's something to publish (unflushed edits,
  // staged changes, or a first-time publishable draft); server blockers gate the
  // rest via can_submit on the save/publish round-trip.
  const publishDisabled = isSaving || (!hasUnsavedChanges && !canPublish);
  // Live + clean: nothing staged, nothing to publish — the Review & Publish tab
  // shows a success confirmation instead of a disabled Publish button.
  const isLivePublished =
    status === "submitted" && !hasUnsavedChanges && !canPublish;

  // Servings are managed on the food page, so a published recipe re-reads them
  // when the window regains focus. Only on the Servings tab and with nothing
  // unsaved: a refetch re-hydrates the form and would overwrite edits.
  const refetchRecipe = recipeQuery.refetch;
  useEffect(() => {
    if (!isPublishedRecipe || activeTab !== "servings" || hasUnsavedChanges) {
      return;
    }
    const onFocus = () => {
      void refetchRecipe();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [isPublishedRecipe, activeTab, hasUnsavedChanges, refetchRecipe]);

  useEffect(() => {
    if (!blockerOverride) return;
    setBlockerOverride(null);
    rotateSubmitIdempotencyKey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formSignature]);

  const handleManualSave = () => {
    if (isReadOnly || isSaving) return;
    performRecipeSave("draft");
  };

  // Flush whatever's typed before the user steps out to add a brand, so the
  // recipe is durably saved/staged and nothing is lost mid-flow.
  const flushDraft = () => {
    if (isReadOnly || isSaving) return;
    if (!hasUnsavedChanges) return;
    performRecipeSave("draft");
  };

  // Tick once a minute so "saved Xs ago" stays fresh without thrashing.
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (lastSavedAt === null) return;
    const id = window.setInterval(() => forceTick((t) => t + 1), 30_000);
    return () => window.clearInterval(id);
  }, [lastSavedAt]);

  // Guard: if the staged edits were made by another clinician, confirm first.
  const handlePublish = () => {
    if (!currentRecipeId || isSaving) return;
    if (!hasUnsavedChanges && !canPublish) return;
    if (draftByOther) {
      setPublishConfirmOpen(true);
      return;
    }
    void doPublish();
  };

  // Publish = stage any unflushed edits, then (re-)POST submit to snapshot a new
  // live food version and retire the previous one. Re-runnable for every edit
  // to a published recipe, not just the first submit (Columbus #392).
  const doPublish = async () => {
    if (!currentRecipeId || isSaving) return;
    if (!hasUnsavedChanges && !canPublish) return;
    setIsSaving(true);
    try {
      let publishVersion = version;
      if (hasUnsavedChanges) {
        const saved = await updateRecipe.mutateAsync({
          id: currentRecipeId,
          version,
          payload: buildWritePayload(),
        });
        hydrateRecipe(saved);
        lastSavedSignature.current = formSignature;
        setLastSavedAt(Date.now());
        setBlockerOverride(saved.blockers ?? null);
        // Staged, but a blocker (e.g. DUPLICATE_FOOD_NAME) means we can't
        // publish yet — stop and let the blocker panel explain.
        if (saved.capabilities?.can_submit !== true) {
          rotateSubmitIdempotencyKey();
          return;
        }
        publishVersion = saved.version;
      }
      const published = await submitRecipe.mutateAsync({
        id: currentRecipeId,
        version: publishVersion,
        idempotencyKey: submitIdempotencyKey,
      });
      hydrateRecipe(published);
      lastSavedSignature.current = formSignature;
      setLastSavedAt(Date.now());
      setBlockerOverride(null);
      rotateSubmitIdempotencyKey();
      setIsEditing(false);
      toast({
        title: "Recipe published",
        description: published.food_code
          ? `Live as ${published.food_code}.`
          : "The new version is now live.",
      });
    } catch (error) {
      rotateSubmitIdempotencyKey();
      try {
        await handleRecipeConflict(error);
      } catch (publishError: any) {
        if (isRecipeBlockerError(publishError)) {
          setBlockerOverride(publishError.blockers);
          return;
        }
        toast({
          title: "Could not publish recipe",
          description: publishError?.message || "Try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!currentRecipeId || !canDelete || deleteRecipe.isPending) return;
    try {
      await deleteRecipe.mutateAsync(currentRecipeId);
      toast({
        title: "Recipe deleted",
        description: "The recipe has been removed.",
      });
      navigate(recipeListPath);
    } catch (error: any) {
      toast({
        title: "Could not delete recipe",
        description: error?.message || "Try again.",
        variant: "destructive",
      });
    }
  };

  // Discard the staged draft → revert the recipe to its last-published version.
  const handleDiscard = async () => {
    if (!currentRecipeId || isSaving || discardRecipe.isPending) return;
    setIsSaving(true);
    try {
      const reverted = await discardRecipe.mutateAsync({
        id: currentRecipeId,
        version,
      });
      hydrateRecipe(reverted);
      lastSavedSignature.current = formSignature;
      setLastSavedAt(Date.now());
      setBlockerOverride(null);
      setIsEditing(false);
      toast({
        title: "Draft discarded",
        description: "Reverted to the published version.",
      });
    } catch (error) {
      try {
        await handleRecipeConflict(error);
      } catch (discardError: any) {
        if (isRecipeBlockerError(discardError)) {
          toast({
            title: "Couldn’t discard",
            description:
              discardError.blockers?.[0]?.message ||
              "This draft can’t be discarded.",
            variant: "destructive",
          });
          return;
        }
        toast({
          title: "Couldn’t discard",
          description: discardError?.message || "Try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  // A submitted recipe is locked until the user opts into editing.
  const isReadOnly = status === "submitted" && !isEditing;
  // True while editing an already-submitted recipe (not a fresh draft).
  const isEditingSubmitted = isEditing && status === "submitted";

  // Per-field edit-lock only when editing a NEW VERSION of a published recipe —
  // fields start locked at the previous version's value; a pencil unlocks one at
  // a time. A fresh first draft stays free-form. (Nothing is permanently locked.)
  const perFieldLock = isEditingSubmitted;
  const fieldLocked = (key: string) => perFieldLock && !editableFields.has(key);
  const toggleFieldEdit = (key: string) =>
    setEditableFields((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const fieldModified = (key: string, current: unknown) =>
    perFieldLock && baselineValuesRef.current[key] !== current;
  const resetToPreviousVersion = () => {
    const b = baselineValuesRef.current;
    setRecipeName((b.name as string) ?? "");
    setHindiName((b.hindiName as string) ?? "");
    setSource((b.source as string) ?? "");
    setSourceUrl((b.sourceUrl as string) ?? "");
    setSourceConfidence((b.sourceConfidence as string) ?? "");
    setBrand((b.brand as string) ?? "");
    setFavourite(Boolean(b.favourite));
    setHomemade(Boolean(b.homemade));
    setNotes((b.notes as string) ?? "");
    setEditableFields(new Set());
  };
  const currentEditableValues: Record<string, unknown> = {
    name: recipeName,
    hindiName,
    source,
    sourceUrl,
    sourceConfidence,
    brand,
    favourite,
    homemade,
    notes,
  };
  const hasFieldEdits =
    perFieldLock &&
    Object.keys(currentEditableValues).some(
      (k) => baselineValuesRef.current[k] !== currentEditableValues[k]
    );

  // Editing a submitted recipe just unlocks the fields in place — no version
  // fork. The form already holds the current values, so this is a UI flip.
  const handleEdit = () => {
    if (!canSave) return;
    setIsEditing(true);
    if (status === "submitted") {
      toast({
        title: "Now editing a new version",
        description:
          "Your edits autosave as a draft and stay unpublished until you Publish — the live version is untouched.",
      });
    }
  };

  const handleCancel = () => {
    // A staged new-version draft persists (it's shared) — leaving just returns
    // to the list; the draft stays for next time. Entering edit on a clean
    // published recipe without staging anything yet returns to the read-only
    // view. A brand-new draft leaves the form.
    if (isEditingSubmitted && !hasUnpublishedChanges) {
      setIsEditing(false);
      return;
    }
    navigate(recipeListPath);
  };

  // Until the recipe is created/loaded and hydrated, show a loader instead of
  // flashing an empty form (opening an existing recipe or minting a new draft).
  if (!hasHydratedCurrentRecipe) {
    return (
      <div className="h-full flex flex-col gap-4">
        <Card className="max-w-[2000px] flex-1 flex items-center justify-center min-h-0">
          {createError ? (
            <div className="text-center space-y-3">
              <p className="z-body text-destructive">
                Couldn’t start a new recipe.
              </p>
              <p className="z-hint text-muted-foreground">{createError}</p>
              <div className="flex items-center justify-center gap-2">
                <Button variant="outline" size="sm" onClick={retryCreateRecipe}>
                  Try again
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(recipeListPath)}
                >
                  Back to recipes
                </Button>
              </div>
            </div>
          ) : recipeQuery.isError ? (
            <div className="text-center space-y-3">
              <p className="z-body text-destructive">
                Couldn’t load this recipe.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(recipeListPath)}
              >
                Back to recipes
              </Button>
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 text-muted-foreground">
              <Loader2 className="size-5 animate-spin" aria-hidden />
              <span className="z-body">Loading recipe…</span>
            </div>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col gap-4">
      <Card className="max-w-[2000px] flex-1 flex flex-col min-h-0 overflow-visible">
        <CardHeader className="shrink-0">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              <CardTitle className="z-h2">
                {recipeName.trim() || "New recipe"}
              </CardTitle>
              <FoodCodeBadge
                code={foodCode}
                isPublished={status === "submitted"}
                versionNumber={recipe?.food_version_number ?? null}
              />
              {/* Read-only + a staged draft → a clickable "Draft in progress"
                  pill that routes into the draft (via a dialog). */}
              {isReadOnly && hasUnpublishedChanges && (
                <DraftInProgressPill
                  targetVersionNumber={draftTarget}
                  editorName={draftByOther ? recipe?.draft_updated_by : null}
                  onClick={() => setNewVersionDialogOpen(true)}
                />
              )}
              {/* Editing a new version. Lazy creation: no draft exists until the
                  first change stages one — so say "New version" on entry and only
                  "· draft · v{n}" once the backend has actually staged it. */}
              {isEditingSubmitted && (
                <Badge variant="warning">
                  {hasUnpublishedChanges
                    ? "New version · draft"
                    : "New version"}
                  {draftTarget != null ? ` · v${draftTarget}` : ""}
                  {draftByOther && recipe?.draft_updated_by
                    ? ` by ${recipe.draft_updated_by}`
                    : ""}
                </Badge>
              )}
              {!isReadOnly && (
                <SaveIndicator
                  isSaving={isSaving}
                  lastSavedAt={lastSavedAt}
                  hasUnsavedChanges={hasUnsavedChanges}
                />
              )}
            </div>
            <div className="flex items-center gap-2">
              {isReadOnly ? (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(recipeListPath)}
                  >
                    Back
                  </Button>
                  {/* Only offer "Create new version" on a clean recipe — when a
                      draft already exists you continue it via the pill (there's
                      one shared draft per recipe; never fork). */}
                  {!hasUnpublishedChanges && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => setNewVersionDialogOpen(true)}
                      disabled={!canSave}
                    >
                      <GitBranch className="size-4" />
                      Create new version
                    </Button>
                  )}
                  {/* A staged draft exists → let it be discarded from here too. */}
                  {hasUnpublishedChanges && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label="More actions"
                        >
                          <MoreVertical className="size-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onSelect={() => setDiscardDialogOpen(true)}
                          disabled={discardRecipe.isPending}
                          className="text-destructive focus:text-destructive"
                        >
                          <RotateCcw className="size-4" />
                          Discard changes
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </>
              ) : (
                <>
                  {/* "Back" when leaving navigates away (nothing to discard —
                      autosave persists the draft); "Cancel" only when it returns
                      to the read-only view. */}
                  <Button variant="ghost" size="sm" onClick={handleCancel}>
                    {isEditingSubmitted && !hasUnpublishedChanges
                      ? "Cancel"
                      : "Back"}
                  </Button>
                  {/* New-version mode: revert Info fields to the previous
                      version's values. */}
                  {perFieldLock && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={resetToPreviousVersion}
                      disabled={!hasFieldEdits}
                    >
                      Reset
                    </Button>
                  )}
                  {/* Save is the primary action (loader inside). Publish now
                      lives in its own "Review & publish" tab so it can't be hit
                      by accident next to Save. Delete (drafts only) is tucked in
                      the overflow menu. */}
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleManualSave}
                    disabled={saveDisabled}
                  >
                    {isSaving ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Save className="size-4" />
                    )}
                    {isSaving ? "Saving…" : "Save"}
                  </Button>
                  {((status !== "submitted" && canDelete) ||
                    (isEditingSubmitted && hasUnpublishedChanges)) && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label="More actions"
                        >
                          <MoreVertical className="size-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {status !== "submitted" && canDelete && (
                          <DropdownMenuItem
                            onSelect={() => setDeleteDialogOpen(true)}
                            disabled={deleteRecipe.isPending}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="size-4" />
                            Delete draft
                          </DropdownMenuItem>
                        )}
                        {isEditingSubmitted && hasUnpublishedChanges && (
                          <DropdownMenuItem
                            onSelect={() => setDiscardDialogOpen(true)}
                            disabled={discardRecipe.isPending}
                            className="text-destructive focus:text-destructive"
                          >
                            <RotateCcw className="size-4" />
                            Discard changes
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-y-auto pt-2 pb-6">
          <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as typeof activeTab)}
          >
            <TabsList>
              <TabsTrigger value="info">
                Info
                <TabCount n={tabBlockerCounts.info} />
              </TabsTrigger>
              <TabsTrigger value="ingredients">
                Ingredients
                <TabCount n={tabBlockerCounts.ingredients} />
              </TabsTrigger>
              <TabsTrigger value="servings">
                Servings
                <TabCount n={tabBlockerCounts.servings} />
              </TabsTrigger>
              <TabsTrigger value="classification">
                Classification &amp; Diet
                <TabCount n={tabBlockerCounts.classification} />
              </TabsTrigger>
              <TabsTrigger value="nutrients">Nutrient Profile</TabsTrigger>
              <TabsTrigger value="label">Nutrition Label</TabsTrigger>
              <TabsTrigger value="publish">
                Review &amp; Publish
                <TabCount n={publishIssues.length} />
              </TabsTrigger>
            </TabsList>

            {/* A submitted recipe renders read-only: the disabled fieldset
                locks every form control inside (native inputs + Radix
                buttons) without touching the tab navigation above. Clicking
                "Edit recipe" unlocks it in place. */}
            <fieldset
              disabled={isReadOnly}
              className="contents disabled:cursor-default"
            >
              {/* ── INFO ──────────────────────────────────────────────── */}
              <TabsContent value="info" className="mt-4 space-y-3">
                <SectionCard
                  title="Identity"
                  info={[
                    "Type the name clinicians will search for. The Hindi name is optional.",
                  ]}
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-3">
                    <EditableField
                      label="Recipe name"
                      required
                      lockable={perFieldLock}
                      unlocked={editableFields.has("name")}
                      modified={fieldModified("name", recipeName)}
                      onToggle={() => toggleFieldEdit("name")}
                    >
                      <Input
                        placeholder="e.g. Moong dal khichdi"
                        value={recipeName}
                        onChange={(e) => setRecipeName(e.target.value)}
                        disabled={fieldLocked("name")}
                      />
                    </EditableField>
                    <EditableField
                      label="Hindi Name"
                      lockable={perFieldLock}
                      unlocked={editableFields.has("hindiName")}
                      modified={fieldModified("hindiName", hindiName)}
                      onToggle={() => toggleFieldEdit("hindiName")}
                    >
                      <Input
                        placeholder="e.g. moong daal ki khichdi"
                        value={hindiName}
                        onChange={(e) => setHindiName(e.target.value)}
                        disabled={fieldLocked("hindiName")}
                      />
                    </EditableField>
                  </div>
                </SectionCard>

                <SectionCard
                  title="Source"
                  info={[
                    "Pick the source and how sure you are of it. Paste a link if there is one.",
                  ]}
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-3">
                    <EditableField
                      label="Source"
                      lockable={perFieldLock}
                      unlocked={editableFields.has("source")}
                      modified={fieldModified("source", source)}
                      onToggle={() => toggleFieldEdit("source")}
                    >
                      <Select
                        value={source}
                        onValueChange={setSource}
                        disabled={isLoadingMetadata || fieldLocked("source")}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              isLoadingMetadata ? "Loading…" : "Select source"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent
                          position="popper"
                          className={SELECT_CONTENT_MAX_H}
                        >
                          {foodSources.map((s) => (
                            <SelectItem key={s.id} value={s.name}>
                              {s.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </EditableField>
                    <EditableField
                      label="Source confidence"
                      lockable={perFieldLock}
                      unlocked={editableFields.has("sourceConfidence")}
                      modified={fieldModified(
                        "sourceConfidence",
                        sourceConfidence
                      )}
                      onToggle={() => toggleFieldEdit("sourceConfidence")}
                    >
                      <Select
                        value={sourceConfidence}
                        onValueChange={setSourceConfidence}
                        disabled={
                          isLoadingMetadata || fieldLocked("sourceConfidence")
                        }
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              isLoadingMetadata
                                ? "Loading…"
                                : "Select source confidence"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent
                          position="popper"
                          className={SELECT_CONTENT_MAX_H}
                        >
                          {sourceConfidenceOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </EditableField>
                    <EditableField
                      label="Source link"
                      helper="Web page or PDF URL."
                      lockable={perFieldLock}
                      unlocked={editableFields.has("sourceUrl")}
                      modified={fieldModified("sourceUrl", sourceUrl)}
                      onToggle={() => toggleFieldEdit("sourceUrl")}
                    >
                      <Input
                        placeholder="https://…"
                        value={sourceUrl}
                        onChange={(e) => setSourceUrl(e.target.value)}
                        disabled={fieldLocked("sourceUrl")}
                      />
                    </EditableField>
                    <EditableField
                      label="Brand"
                      lockable={perFieldLock}
                      unlocked={editableFields.has("brand")}
                      modified={fieldModified("brand", brand)}
                      onToggle={() => toggleFieldEdit("brand")}
                    >
                      <RecipeBrandSelect
                        value={brand}
                        onValueChange={setBrand}
                        brands={foodBrands}
                        isLoading={isLoadingMetadata}
                        disabled={fieldLocked("brand")}
                        onBeforeCreate={flushDraft}
                      />
                    </EditableField>
                  </div>
                </SectionCard>

                <SectionCard
                  title="Flags & notes"
                  info={[
                    "Switch on Favorite or Homemade if they apply. Notes are for clinicians.",
                  ]}
                >
                  <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
                    <ToggleRow
                      label="Favorite"
                      checked={favourite}
                      onCheckedChange={setFavourite}
                    />
                    <ToggleRow
                      label="Homemade"
                      checked={homemade}
                      onCheckedChange={setHomemade}
                    />
                  </div>

                  <FieldRow
                    label="Notes"
                    helper="Clinician-only. Cooking method, soaking time, swaps."
                  >
                    <Textarea
                      rows={3}
                      placeholder="…"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </FieldRow>
                </SectionCard>
              </TabsContent>

              {/* ── INGREDIENTS ───────────────────────────────────────── */}
              <TabsContent value="ingredients" className="mt-4 space-y-3">
                <Card size="sm">
                  <CardHeader>
                    <CardTitle className="z-h3 flex items-center gap-2">
                      <span>
                        Ingredients
                        <span className="text-destructive"> *</span>
                      </span>
                      {ingredients.length > 0 && (
                        <Badge variant="neutral">{ingredients.length}</Badge>
                      )}
                      <InfoPopover
                        title="Ingredients"
                        lines={[
                          "Click Add ingredient, pick a food, then set its quantity and serving size.",
                          "Leave Retention at 100% unless cooking loses nutrients.",
                        ]}
                      />
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {ingredients.length === 0 ? (
                      <div className="rounded-lg border border-dashed border-border bg-muted/20 px-6 py-10 text-center space-y-3">
                        <p className="z-body text-muted-foreground">
                          No ingredients yet — at least one is required to
                          submit.
                        </p>
                        <Button size="sm" onClick={() => setPickerOpen(true)}>
                          <Plus className="size-4" />
                          Add ingredient
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-[16px_1fr_104px_220px_96px_32px] items-center gap-3 px-3 pb-2 border-b border-border/60 z-eyebrow text-muted-foreground">
                          <span />
                          <span>Food item</span>
                          <span className="text-center">Quantity</span>
                          <span>Serving size</span>
                          <span className="text-center">Retention</span>
                          <span />
                        </div>
                        {ingredients.map((row) => (
                          <IngredientRowItem
                            key={row.id}
                            row={row}
                            classification={ingredientTagsByFoodId.get(
                              row.foodId
                            )}
                            onChange={(patch) => updateRow(row.id, patch)}
                            onRemove={() => removeRow(row.id)}
                          />
                        ))}
                        <div className="pt-2 flex justify-end">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPickerOpen(true)}
                          >
                            <Plus className="size-4" />
                            Add ingredient
                          </Button>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* ── CLASSIFICATION ────────────────────────────────────── */}
              <TabsContent value="classification" className="mt-4 space-y-3">
                {/* Diet Suitability is system-derived, not a tag the user
                  edits — so it sits in its own card above the tag picker,
                  alongside Texture. Only the claims the recipe actually
                  qualifies for are shown. */}
                <Card size="sm">
                  <CardContent>
                    <FieldRow
                      label="Diet Suitability"
                      info={[
                        "These fill in on their own. Change the allergen tags to change them.",
                      ]}
                    >
                      <DerivedDietFlagsRow
                        states={dietFlagStates}
                        isEmpty={ingredients.length === 0}
                      />
                    </FieldRow>
                  </CardContent>
                </Card>

                <Card size="sm">
                  <CardContent>
                    {/* Two-column grid keeps the tag categories compact —
                      they're short label/chip rows, so a single stacked
                      column wastes horizontal space. */}
                    <div className="flex items-center gap-1.5 pb-1">
                      <span className="z-label">Tags</span>
                      <InfoPopover
                        title="Tags"
                        lines={[
                          "Click a field to pick the tags that apply.",
                          "Tags with a lock come from the ingredients and can't be removed.",
                        ]}
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 md:gap-x-8">
                      {classificationRows.map((row) => {
                        const isAllergen = row.label === "Allergen Tags";
                        const isAge = row.label === "Age Suitability";
                        const isContext = row.label === "Context Tags";
                        const isCondition =
                          row.label === "Condition Suitability";
                        const isMealType = row.label === "Meal Type";
                        return (
                          <div key={row.label}>
                            <TagPickerRow
                              label={row.label}
                              options={row.options}
                              selectedIds={row.selectedIds}
                              onChange={row.onChange}
                              isLoading={isLoadingMetadata}
                              required={isMealType}
                              isModified={isMealType && mealTypeMissing}
                              derivedIds={
                                isAllergen
                                  ? autoAllergen.ids
                                  : isAge
                                    ? autoAge.ids
                                    : isContext
                                      ? inheritedContextIds
                                      : undefined
                              }
                              lockedIds={
                                isAllergen
                                  ? autoAllergen.lockedIds
                                  : isContext
                                    ? inheritedContextIds
                                    : undefined
                              }
                              originByTagId={
                                isAllergen
                                  ? autoAllergen.origin
                                  : isAge
                                    ? autoAge.origin
                                    : undefined
                              }
                              onLockedRemoveAttempt={
                                isAllergen
                                  ? handleLockedAllergenRemove
                                  : isContext
                                    ? handleInheritedContextRemove
                                    : undefined
                              }
                            />
                            {isMealType && mealTypeMissing && (
                              <p className="px-1 pb-2 -mt-1 z-caption text-warning">
                                Required to publish — pick at least one so the
                                recipe is findable in meal-plan search.
                              </p>
                            )}
                            {isCondition && conditionAdvisories.length > 0 && (
                              <div className="px-1 pb-2 -mt-1 space-y-1">
                                {conditionAdvisories.map((adv) => (
                                  <p
                                    key={adv.tagName}
                                    className="z-caption text-warning bg-warning/10 border border-warning/30 rounded-md px-2 py-1.5"
                                  >
                                    <span className="font-medium">
                                      Heads up on “{adv.tagName}”:
                                    </span>{" "}
                                    {adv.message}
                                  </p>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                <Card size="sm">
                  <CardContent>
                    <FieldRow label="Texture" required>
                      <Select
                        value={texture}
                        onValueChange={setTexture}
                        disabled={isLoadingMetadata}
                      >
                        <SelectTrigger
                          className={cn(
                            "md:max-w-xs",
                            textureMissing &&
                              "border-warning ring-1 ring-warning/40"
                          )}
                        >
                          <SelectValue
                            placeholder={
                              isLoadingMetadata ? "Loading…" : "Select texture"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent
                          position="popper"
                          className={SELECT_CONTENT_MAX_H}
                        >
                          {textureOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {textureMissing && (
                        <p className="z-caption text-warning">
                          Required to publish — clinical suitability (dysphagia
                          / tube-feeding).
                        </p>
                      )}
                    </FieldRow>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* ── NUTRIENT PROFILE ──────────────────────────────────── */}
              <TabsContent value="nutrients" className="mt-4 space-y-3">
                <div className="flex flex-wrap items-end justify-between gap-3">
                  <div>
                    <h3 className="z-h3 flex items-center gap-1.5">
                      Nutrient profile
                      <InfoPopover
                        title="Nutrient profile"
                        lines={[
                          "Use the toggle to see values per serving, per 100 g or for the full recipe.",
                          "To change a value, edit the ingredients.",
                        ]}
                      />
                    </h3>
                    <p className="z-caption text-muted-foreground">
                      {nutrientBasisHelper}
                    </p>
                  </div>
                  {/* Remount per basis so a switch can't leave a stale pressed
                      state on the previously selected item. */}
                  <ToggleGroup
                    key={nutrientBasis}
                    type="single"
                    variant="outline"
                    value={nutrientBasis}
                    onValueChange={(value) =>
                      value && setNutrientBasis(value as NutrientBasis)
                    }
                  >
                    <ToggleGroupItem value="perServe">
                      Per serving
                    </ToggleGroupItem>
                    <ToggleGroupItem value="per100g">Per 100 g</ToggleGroupItem>
                    <ToggleGroupItem value="perRecipe">
                      Whole recipe
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                  {displayGroups.map((group) => (
                    <ReadOnlyNutrientTable
                      key={group.title}
                      title={group.title}
                      rows={group.rows}
                    />
                  ))}
                </div>
              </TabsContent>

              {/* ── SERVINGS ──────────────────────────────────────────── */}
              <TabsContent value="servings" className="mt-4">
                <Card size="sm">
                  <CardContent className="space-y-5">
                    {/* Yield is typed; the per-serving weight derives from the
                      ingredient total. Named servings are added in the table. */}
                    <FieldRow
                      label="Yield"
                      helperUnderLabel
                      helper="How many servings the full recipe makes."
                      info={["Type how many servings the full recipe makes."]}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="z-body text-muted-foreground">
                          This recipe makes
                        </span>
                        <Input
                          type="number"
                          inputMode="numeric"
                          min={1}
                          className="w-20 text-center"
                          value={servingsPerRecipe}
                          onChange={(e) => setServingsPerRecipe(e.target.value)}
                          // Normalize on blur so the field can't linger on an
                          // empty/0/negative/decimal value while the effective
                          // yield is silently clamped to a valid integer.
                          onBlur={() =>
                            setServingsPerRecipe(String(servingsCount))
                          }
                        />
                        <span className="z-body text-muted-foreground">
                          {servingsCount === 1 ? "serving." : "servings."}
                        </span>
                      </div>
                    </FieldRow>

                    {hasWeight && (
                      <p className="z-secondary text-muted-foreground">
                        Full recipe weighs{" "}
                        <span className="font-medium text-foreground">
                          {Math.round(fullWeightG)} g
                        </span>{" "}
                        according to the saved recipe totals.
                      </p>
                    )}

                    {/* "Servings" only flips how the table reads. */}
                    <FieldRow
                      label="Servings"
                      helperUnderLabel
                      helper={
                        servingMode === "serving"
                          ? "Show units as how many fit in the full recipe."
                          : "Show units as how many grams one of each weighs."
                      }
                    >
                      <RadioGroup
                        value={servingMode}
                        onValueChange={(v) => setServingMode(v as ServingMode)}
                        className="flex flex-wrap gap-6"
                      >
                        <label className="flex items-center gap-2 cursor-pointer">
                          <RadioGroupItem value="serving" />
                          <span className="z-secondary">Serving based</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <RadioGroupItem value="weight" />
                          <span className="z-secondary">Weight based</span>
                        </label>
                      </RadioGroup>
                    </FieldRow>

                    <RecipeServingsTable
                      mode={servingMode}
                      coreRows={coreServingRows}
                      value={recipeServings}
                      onChange={setRecipeServings}
                      foodRows={foodServingRows}
                      readOnly={isPublishedRecipe}
                      onManageServings={
                        canManageServings ? openFoodServings : undefined
                      }
                    />
                  </CardContent>
                </Card>
              </TabsContent>

              {/* ── NUTRITION LABEL ───────────────────────────────────── */}
              <TabsContent value="label" className="mt-4">
                <div className="space-y-3">
                  <div>
                    <h3 className="z-h3">FSSAI nutrition label</h3>
                    <p className="z-caption text-muted-foreground">
                      Print-style artifact. Follows Codex CAC/GL 2-1985.
                    </p>
                  </div>
                  <NutritionLabelFSSAI
                    productName={recipeName.trim() || undefined}
                    servingLabel={defaultServingLabel}
                    servingsPerPackage={Math.max(Math.round(servingsCount), 1)}
                    labelRows={normalizeLabelRows(recipe?.label_rows ?? [])}
                  />
                </div>
              </TabsContent>
            </fieldset>

            {/* Publish lives in its own tab, OUTSIDE the read-only fieldset, so
                a published recipe with staged changes can still publish them
                without unlocking every field. */}
            <TabsContent value="publish" className="mt-4">
              {isLivePublished ? (
                // Success / live confirmation — the "conclusion" after publish.
                <Card size="sm">
                  <CardContent className="flex flex-col items-center gap-4 py-8 text-center">
                    <span className="inline-flex size-12 items-center justify-center rounded-full bg-success/15 text-success">
                      <Check className="size-6" aria-hidden />
                    </span>
                    <div className="space-y-1">
                      <CardTitle className="z-h3 flex items-center justify-center gap-1.5">
                        Published — live
                        <InfoPopover
                          title="Published"
                          lines={[
                            "Edit any tab, then click Publish new version to update it.",
                            "To drop unpublished edits, use Discard changes in the ⋯ menu.",
                          ]}
                        />
                      </CardTitle>
                      <p className="z-caption text-muted-foreground max-w-md">
                        <span className="font-medium text-foreground">
                          {recipeName.trim() || "This recipe"}
                        </span>{" "}
                        is live
                        {foodCode ? (
                          <>
                            {" "}
                            as{" "}
                            <span className="font-medium text-foreground">
                              {foodCode}
                              {recipe?.food_version_number != null
                                ? ` · v${recipe.food_version_number}`
                                : ""}
                            </span>
                          </>
                        ) : (
                          ""
                        )}
                        . It can now be selected in meal plans and diet recalls.
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(recipeListPath)}
                      >
                        View all recipes
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => setNewVersionDialogOpen(true)}
                        disabled={!canSave}
                      >
                        <GitBranch className="size-4" />
                        Create new version
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card size="sm">
                  <CardContent className="space-y-4">
                    <div>
                      <CardTitle className="z-h3 flex items-center gap-1.5">
                        Review &amp; publish
                        <InfoPopover
                          title="Review & publish"
                          lines={[
                            "Fix anything listed below, then click Publish.",
                          ]}
                        />
                      </CardTitle>
                      <p className="z-caption text-muted-foreground mt-1">
                        Publishing makes this recipe a live food item,
                        selectable in meal plans and diet recalls.{" "}
                        {status === "submitted"
                          ? "It keeps the same food code; the current live version is retired (kept for existing records) and this becomes live."
                          : "It's assigned a food code and goes live."}
                      </p>
                    </div>

                    {publishIssues.length > 0 ? (
                      <div className="rounded-md border border-warning/30 bg-warning/10 px-3 py-2">
                        <p className="z-label text-warning">
                          Can’t publish yet
                        </p>
                        <ul className="mt-1 space-y-1">
                          {publishIssues.map((message) => (
                            <li
                              key={message}
                              className="z-caption text-warning"
                            >
                              {message}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <div className="rounded-md border border-success/30 bg-success/10 px-3 py-2">
                        <p className="z-caption text-success inline-flex items-center gap-1.5">
                          <Check className="size-3.5" aria-hidden />
                          {status === "submitted"
                            ? "You have unpublished changes ready to publish."
                            : "This recipe is ready to publish."}
                        </p>
                      </div>
                    )}

                    <div className="flex justify-end pt-1">
                      <Button
                        type="button"
                        onClick={handlePublish}
                        disabled={publishDisabled}
                      >
                        {isSaving ? (
                          <Loader2 className="size-4 animate-spin" />
                        ) : (
                          <Upload className="size-4" />
                        )}
                        {status === "submitted"
                          ? "Publish new version"
                          : "Publish"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <AlertDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          setDeleteDialogOpen(open);
          if (!open) setDeleteConfirmText("");
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete draft recipe?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the draft{" "}
              <span className="font-medium text-foreground">
                “{recipeName.trim() || "Untitled recipe"}”
              </span>
              . It hasn’t been published, so nothing that references a live food
              item is affected — but this can’t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <Label className="z-secondary">
              Type the recipe name to confirm
            </Label>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder={recipeName.trim() || "recipe name"}
              autoFocus
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteRecipe.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={
                deleteRecipe.isPending ||
                deleteConfirmText.trim() !== recipeName.trim()
              }
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteRecipe.isPending ? "Deleting…" : "Delete draft"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={newVersionDialogOpen}
        onOpenChange={setNewVersionDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {hasUnpublishedChanges
                ? "Continue the draft in progress?"
                : "Create a new version?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {hasUnpublishedChanges ? (
                <>
                  This recipe has an unpublished draft
                  {draftByOther && recipe?.draft_updated_by ? (
                    <>
                      {" "}
                      last edited by{" "}
                      <span className="font-medium text-foreground">
                        {recipe.draft_updated_by}
                      </span>
                      {recipe?.draft_updated_at
                        ? ` (${formatRelativeTime(
                            new Date(recipe.draft_updated_at).getTime()
                          )})`
                        : ""}
                    </>
                  ) : null}
                  . Drafts are shared — there’s one per recipe, so you’ll
                  continue this same one
                  {draftTarget != null
                    ? ` (will publish as v${draftTarget})`
                    : ""}
                  . Publishing it makes it live and retires the current version.
                </>
              ) : (
                <>
                  You’ll edit a draft of{" "}
                  <span className="font-medium text-foreground">
                    “{recipeName.trim() || "this recipe"}”
                  </span>
                  . The current version stays live and unchanged while you work.
                  When you publish, this becomes the live version and the
                  previous one is retired — kept for existing meal plans and
                  records, just no longer offered for new ones. The food code
                  stays the same.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleEdit}>
              {hasUnpublishedChanges ? (
                <>
                  <Pencil className="size-4" />
                  Continue editing
                </>
              ) : (
                <>
                  <GitBranch className="size-4" />
                  Create new version
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={publishConfirmOpen}
        onOpenChange={setPublishConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Publish someone else’s changes?</AlertDialogTitle>
            <AlertDialogDescription>
              The staged edits to this recipe were last made by{" "}
              <span className="font-medium text-foreground">
                {recipe?.draft_updated_by ?? "another user"}
              </span>
              {recipe?.draft_updated_at
                ? ` (${formatRelativeTime(
                    new Date(recipe.draft_updated_at).getTime()
                  )})`
                : ""}
              . Recipe drafts are shared, so this publishes their work — it
              becomes the live version and retires the current one. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void doPublish()}>
              <Upload className="size-4" />
              Publish anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={discardDialogOpen} onOpenChange={setDiscardDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard the draft?</AlertDialogTitle>
            <AlertDialogDescription>
              This throws away the unpublished changes
              {draftTarget != null ? ` (draft v${draftTarget})` : ""} and
              reverts to the published version
              {recipe?.food_version_number != null
                ? ` (v${recipe.food_version_number})`
                : ""}
              . It can’t be undone. The live food item is unaffected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={discardRecipe.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDiscard}
              disabled={discardRecipe.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {discardRecipe.isPending ? "Discarding…" : "Discard changes"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <RecipeFoodPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onAdd={handlePickerAdd}
      />
    </div>
  );
};

interface IngredientRowItemProps {
  row: IngredientRow;
  /** The ingredient as the backend last resolved it; absent for a new row. */
  classification?: RecipeIngredient;
  onChange: (patch: Partial<IngredientRow>) => void;
  onRemove: () => void;
}

/**
 * One ingredient's own allergens and the diets they rule out. An untagged
 * ingredient shows nothing: that is "not tagged", not "safe".
 */
const IngredientClassification = ({
  classification,
}: {
  classification?: RecipeIngredient;
}) => {
  const allergens = classification?.allergen_tags ?? [];
  if (allergens.length === 0) return null;
  const states = toDietFlagStates(classification?.diet_flags, () => "");
  const ruledOut = DIET_FLAG_ORDER.filter(
    (key) => states[key].status === "fail"
  );

  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      {allergens.map((tag) => (
        <span
          key={tag.id}
          title={`Contains ${tag.name}`}
          className="inline-flex min-h-5 items-center rounded-full border border-warning/40 bg-warning/10 px-2 z-hint text-warning"
        >
          {tag.name}
        </span>
      ))}
      {ruledOut.map((key) => (
        <DietFlagChip
          key={key}
          label={DIET_FLAG_RULED_OUT_LABELS[key]}
          state={states[key]}
        />
      ))}
    </div>
  );
};

const RETENTION_INPUT_PATTERN = /^$|^\d{1,3}$/;

const IngredientRowItem = ({
  row,
  classification,
  onChange,
  onRemove,
}: IngredientRowItemProps) => {
  const [localQty, setLocalQty] = useState(row.quantity.toString());
  const [isDirty, setIsDirty] = useState(false);
  const [servingOpen, setServingOpen] = useState(false);

  const retentionPct = Math.round(row.retentionFactor * 100);
  const [localRetention, setLocalRetention] = useState(retentionPct.toString());
  const [isRetentionDirty, setIsRetentionDirty] = useState(false);

  const { data: servings = [], isLoading: servingsLoading } = useFoodServings(
    row.foodId,
    servingOpen
  );

  const handleQtyChange = (value: string) => {
    if (!quantityPatternFor(row.requiresWholeServings).test(value)) return;
    setLocalQty(value);
    setIsDirty(value !== row.quantity.toString());
  };

  const handleQtyCommit = () => {
    if (!isDirty) return;
    if (localQty === "" || localQty.endsWith(".")) {
      setLocalQty(row.quantity.toString());
      setIsDirty(false);
      return;
    }
    onChange({ quantity: parseFloat(localQty) });
    setIsDirty(false);
  };

  const handleRetentionChange = (value: string) => {
    if (!RETENTION_INPUT_PATTERN.test(value)) return;
    setLocalRetention(value);
    setIsRetentionDirty(value !== retentionPct.toString());
  };

  const handleRetentionCommit = () => {
    if (!isRetentionDirty) return;
    if (localRetention === "") {
      setLocalRetention(retentionPct.toString());
      setIsRetentionDirty(false);
      return;
    }
    const next = Math.min(Math.max(parseInt(localRetention, 10), 0), 100);
    setLocalRetention(next.toString());
    onChange({ retentionFactor: next / 100 });
    setIsRetentionDirty(false);
  };

  const handleServingChange = (servingId: string) => {
    const serving = servings.find((s) => s.id.toString() === servingId);
    if (!serving) return;
    onChange({
      servingId: serving.id,
      servingLabel:
        serving.description || serving.serving_unit?.name || "Serving",
      servingMultiplier: parseFloat(serving.multiplier),
      servingWeightG: parseFloat(serving.weight_g) || 0,
    });
    setServingOpen(false);
  };

  return (
    <div className="grid grid-cols-[16px_1fr_104px_220px_96px_32px] items-center gap-3 px-3 py-2 hover:bg-muted/30 rounded-lg transition-colors group">
      <Apple className="size-4 fill-primary text-primary" />
      <div className="min-w-0">
        <div className="z-body break-words leading-snug">{row.foodName}</div>
        <IngredientClassification classification={classification} />
      </div>

      <div className="flex items-center gap-1">
        <Input
          type="text"
          value={localQty}
          onChange={(e) => handleQtyChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleQtyCommit();
            if (e.key === "Escape") {
              setLocalQty(row.quantity.toString());
              setIsDirty(false);
            }
          }}
          className={cn(
            "h-9 px-2 text-center flex-1 min-w-0",
            isDirty && "border-primary ring-2 ring-primary/20 bg-primary/5"
          )}
        />
        {isDirty && (
          <Button
            type="button"
            size="icon-sm"
            onClick={handleQtyCommit}
            aria-label="Save quantity"
          >
            <Check className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      <Select
        open={servingOpen}
        onOpenChange={setServingOpen}
        value={row.servingId.toString()}
        onValueChange={handleServingChange}
      >
        <SelectTrigger className="data-[size=default]:h-auto min-h-9 w-full whitespace-normal py-1.5 text-left [&>[data-slot=select-value]]:line-clamp-none [&>[data-slot=select-value]]:text-left">
          <SelectValue>{row.servingLabel}</SelectValue>
        </SelectTrigger>
        <SelectContent position="popper" className={SELECT_CONTENT_MAX_H}>
          {servingsLoading ? (
            <div className="px-3 py-4 flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span className="z-caption">Loading servings…</span>
            </div>
          ) : (
            servings.map((serving) => (
              <SelectItem key={serving.id} value={serving.id.toString()}>
                {serving.description || serving.serving_unit?.name || "Serving"}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>

      <div className="flex items-center gap-1">
        <div className="relative flex-1 min-w-0">
          <Input
            type="text"
            inputMode="numeric"
            value={localRetention}
            onChange={(e) => handleRetentionChange(e.target.value)}
            onBlur={handleRetentionCommit}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleRetentionCommit();
              if (e.key === "Escape") {
                setLocalRetention(retentionPct.toString());
                setIsRetentionDirty(false);
              }
            }}
            aria-label="Retention factor"
            title="Share of nutrients retained after cooking (0–100%)"
            className={cn(
              "h-9 pl-2 pr-6 text-center w-full",
              isRetentionDirty &&
                "border-primary ring-2 ring-primary/20 bg-primary/5"
            )}
          />
          <span
            className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 z-caption text-muted-foreground"
            aria-hidden
          >
            %
          </span>
        </div>
        {isRetentionDirty && (
          <Button
            type="button"
            size="icon-sm"
            onClick={handleRetentionCommit}
            aria-label="Save retention"
          >
            <Check className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon-sm" aria-label="Row actions">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onSelect={onRemove}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
            Remove
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

const SaveIndicator = ({
  isSaving,
  lastSavedAt,
  hasUnsavedChanges,
}: {
  isSaving: boolean;
  lastSavedAt: number | null;
  hasUnsavedChanges: boolean;
}) => {
  // The Save button owns the in-progress spinner; this is just the resting hint.
  if (isSaving) return null;

  if (hasUnsavedChanges) {
    return (
      <span className="z-caption text-muted-foreground inline-flex items-center gap-1.5">
        <span className="size-1.5 rounded-full bg-warning" aria-hidden />
        Unsaved changes
      </span>
    );
  }

  if (lastSavedAt !== null) {
    return (
      <span className="z-caption text-muted-foreground inline-flex items-center gap-1.5">
        <span className="size-1.5 rounded-full bg-success" aria-hidden />
        Saved {formatRelativeTime(lastSavedAt)}
      </span>
    );
  }

  return null;
};

const SectionCard = ({
  title,
  description,
  info,
  children,
}: {
  title: string;
  description?: string;
  info?: string[];
  children: React.ReactNode;
}) => (
  <Card size="sm">
    <CardHeader>
      <CardTitle className="z-h3 flex items-center gap-1.5">
        {title}
        {info && <InfoPopover title={title} lines={info} />}
      </CardTitle>
      {description && (
        <p className="z-caption text-muted-foreground">{description}</p>
      )}
    </CardHeader>
    <CardContent className="space-y-3">{children}</CardContent>
  </Card>
);

const ToggleRow = ({
  label,
  checked,
  onCheckedChange,
}: {
  label: string;
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
}) => (
  <label className="flex items-center gap-3 cursor-pointer select-none">
    <Switch checked={checked} onCheckedChange={onCheckedChange} />
    <span className="z-secondary">{label}</span>
  </label>
);

const FieldRow = ({
  label,
  helper,
  info,
  required,
  helperUnderLabel,
  children,
}: {
  label: string;
  helper?: string;
  // Extra explanation shown in a click-to-open "i" next to the label.
  info?: string[];
  required?: boolean;
  // Render the helper directly beneath the label (as a subheading caption)
  // instead of below the control. Used where the explanation reads better up top.
  helperUnderLabel?: boolean;
  children: React.ReactNode;
}) => (
  <div className="space-y-1.5">
    <div className="flex items-center gap-1.5">
      <Label className="z-secondary">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </Label>
      {info && <InfoPopover title={label} lines={info} />}
    </div>
    {helperUnderLabel && helper && (
      <p className="z-caption text-muted-foreground">{helper}</p>
    )}
    {children}
    {!helperUnderLabel && helper && (
      <p className="z-caption text-muted-foreground">{helper}</p>
    )}
  </div>
);

export default AddRecipeForm;
